import { Chip, Switch, TextField, Typography } from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import PropTypes from "prop-types"
import { useContext, useEffect, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import { listClients } from "~/actions/clients"
import CounterHeader from "~/components/Common/CounterHeader"
import Tag from "~/components/Common/Tag"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import PathContext from "~/contexts/Path"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "~/components/Common/Box"
import ClientMultiSelect from "~/components/Common/ClientMultiSelect"
import useStyles from "./styles"

const PathDetails = ({ tags }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { data: clients } = useQuery("clients", () => listClients())
  const { openSnackBar, setPageSubTitle, authUser } = useContext(AppContext)
  const {
    path,
    setDebouncedDetails,
    debouncedDetails,
    pathDetailsValues,
    setPathDetailsValues,
    setInitialDetailsContext,
    tryPublish,
    trySave,
    lengthError,
    tagsContext,
    setTagsContext,
    updatePathMutation,
    categoriesContext,
    setCategoriesContext,
  } = useContext(PathContext)
  const debouncedLocalValues = useDebounce(pathDetailsValues, 200)
  const [tagAutocompleteValue, setTagAutocompleteValue] = useState("")
  const [tagTextFieldValue, setTagTextFieldValue] = useState("")
  const openErrorSnackbar = useOpenErrorSnackbar()
  //show errors if inputs are empty or more than allow characters
  const descriptionError =
    ((debouncedDetails?.description === "" ||
      debouncedDetails?.description?.length > 2000) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.description?.length > 2000)

  const nameError =
    ((debouncedDetails?.name === "" || debouncedDetails?.name?.length > 50) &&
      (trySave || tryPublish)) ||
    (lengthError && debouncedDetails?.name?.length > 50)

  const headlineError =
    ((debouncedDetails?.headline === "" ||
      debouncedDetails?.headline?.length > 100) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.headline?.length > 100)

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image = JSON.stringify({
      url: uploadRes.data.url,
      secure_url: uploadRes.data.secure_url,
      originial_filename: uploadRes.data.original_filename,
      version: uploadRes.data.version,
      format: uploadRes.data.format,
      public_id: uploadRes.data.public_id,
    })
    const data = {
      [type]: image,
    }
    updatePathMutation.mutate(data, {
      onSuccess: (res) => {
        setTimeout(() => {
          loadingAction(false)
        }, 400)
        serialize("path", res.data).then((serializedData) => {
          return queryClient.setQueryData("paths", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                //only update image to dont loss data that it is not save
                x[type] = serializedData[type]
                return x
              }
            })
            return [...newArray]
          })
        })
      },
      onError: (err) => {
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Thumbnail")
      },
    })
  }

  //set learning path subtitle to empty string
  useEffect(() => {
    setPageSubTitle("")
  }, [])

  //delete tag from the tags array in  learning path context
  const deleteTag = (name) => {
    setTagsContext(tagsContext?.filter((x) => x.name !== name))
  }

  // set learning path details to compoment state
  useEffect(() => {
    if (path?.id) {
      setPathDetailsValues({
        name: path.name,
        description: path.description || "",
        headline: path.headline || "",
        is_public: path?.is_public ? true : false,
        lock_order: path?.lock_order ? true : false,
        discover_page: path?.discover_page ? true : false,
        color: path?.color || "",
        clients: path?.clients || [],
      })
      setInitialDetailsContext({
        name: path.name,
        description: path.description || "",
        headline: path.headline || "",
        is_public: path?.is_public ? true : false,
        lock_order: path?.lock_order ? true : false,
        discover_page: path?.discover_page ? true : false,
        color: path?.color || "",
        clients: path?.clients || [],
      })
    }
  }, [path])

  //set details context state from this component state using debounce
  useEffect(() => {
    setDebouncedDetails(pathDetailsValues)
  }, [debouncedLocalValues])

  const deleteCategory = (name) => {
    setCategoriesContext(categoriesContext?.filter((x) => x.name !== name))
  }

  const updateClients = (newClients) => {
    setPathDetailsValues((oldState) => ({
      ...oldState,
      clients: newClients,
    }))
  }

  const renderCategories = () => {
    return (
      <>
        <Typography color="secondary" className={classes.inputLabel}>
          Categories
        </Typography>

        <Box
          display="flex"
          flexWrap="wrap"
          style={{ height: "100%" }}
          marginBottom="16px"
        >
          {categoriesContext?.map((category, i) => {
            return (
              <Chip
                key={i}
                label={category.name}
                size="small"
                color="primary"
                style={{
                  marginRight: "8px",
                  marginTop: "8px",
                }}
              />
            )
          })}
        </Box>
      </>
    )
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Learning Path Details
      </Typography>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Learning Path Name"
            value={debouncedDetails?.name}
            max={50}
          />
          <TextField
            value={pathDetailsValues?.name || ""}
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                name: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {nameError ? (
            <Typography className={classes.requiredText}>
              Name is required and it has to be less than 50 characters
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Headline"
            value={debouncedDetails?.headline}
            max={100}
          />
          <TextField
            value={pathDetailsValues?.headline || ""}
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                headline: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {headlineError ? (
            <Typography className={classes.requiredText}>
              Headline is required and it has to be less than 255 characters
            </Typography>
          ) : null}
        </Box>
      </div>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Badge Overview"
            value={debouncedDetails?.description}
            max={2000}
          />
          <TextField
            value={pathDetailsValues.description || ""}
            multiline
            maxRows={10}
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                description: e.target.value,
              }))
            }}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: classes.inputDescription,
              },
            }}
            size="small"
            fullWidth
          />
          {descriptionError ? (
            <Typography className={classes.requiredText}>
              Badge overview is required and it has to be less than 2000
              characters
            </Typography>
          ) : null}
        </Box>{" "}
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          {/* <DateTime /> */}
          <Typography color="secondary" className={classes.inputLabel}>
            Search Terms
          </Typography>
          <Autocomplete
            id="tags-autocomplete"
            options={tags || []}
            renderInput={(params) => <TextField {...params} variant="filled" />}
            value={tagAutocompleteValue}
            onChange={(e, val) => setTagAutocompleteValue(val)}
            getOptionLabel={(option) => option.name || option}
            getOptionDisabled={(option) =>
              !!tagsContext.find((y) => y.name === option.name)
            }
            freeSolo
            openOnFocus
            onInputChange={(e, val) => {
              setTagTextFieldValue(val)
            }}
            inputValue={tagTextFieldValue}
            style={{
              width: "100%",
              backgroundColor: "#F4F4F4",
              borderRadius: "4px",
            }}
            onKeyPress={(e) => {
              if (
                e.key === "Enter" &&
                (tagTextFieldValue !== "" || tagAutocompleteValue !== "")
              ) {
                //check if we are using the same work with capital letters or spaces
                if (
                  tags.find(
                    (y) =>
                      y.name.replace(/\s+/g, "").toLowerCase() ===
                      tagTextFieldValue.replace(/\s+/g, "").toLowerCase(),
                  ) &&
                  tagsContext.find(
                    (y) =>
                      y.name.replace(/\s+/g, "").toLowerCase() ===
                      tagTextFieldValue.replace(/\s+/g, "").toLowerCase(),
                  )
                ) {
                  setTagAutocompleteValue("")
                  setTagTextFieldValue("")
                } else {
                  const found = tags.find(
                    (y) =>
                      y.name.replace(/\s+/g, "").toLowerCase() ===
                      tagTextFieldValue.replace(/\s+/g, "").toLowerCase(),
                  )
                  if (found) {
                    setTagsContext([...tagsContext, found])
                    setTagAutocompleteValue("")
                    setTagTextFieldValue("")
                  } else {
                    setTagAutocompleteValue("")
                    setTagTextFieldValue("")
                    setTagsContext([
                      ...tagsContext,
                      typeof tagAutocompleteValue === "object"
                        ? tagAutocompleteValue
                        : { name: tagAutocompleteValue },
                    ])
                  }
                }
              }
            }}
            size="small"
            classes={{
              inputRoot: classes.autocompleteRoot,
            }}
          />
          {tagsContext?.length === 0 && tryPublish ? (
            <Typography className={classes.requiredText}>
              Search Terms are required
            </Typography>
          ) : null}
          <Box
            display="flex"
            flexWrap="wrap"
            style={{ height: "100%" }}
            marginBottom="16px"
          >
            {tagsContext?.map((x, i) => {
              return (
                <Tag
                  key={i}
                  tagName={x.name || x}
                  deleteTag={() => deleteTag(x.name)}
                />
              )
            })}
          </Box>
        </Box>
      </div>
      <div className={classes.twoFieldContainer}>
        {/* //? Switched order around of switches and categories */}
        {/* <Box display="flex" flexDirection={"column"} width={"100%"}>
          {renderCategories()}
        </Box> */}
        {/* <Box className={classes.switchContainer}> */}
        <Box display="flex" flexDirection={""} width={"100%"}>
          <Box marginTop="8px" width="48%">
            <Typography color="secondary" className={classes.inputLabel}>
              Lock Order
            </Typography>
            <Switch
              onChange={(e) => {
                setPathDetailsValues((oldState) => ({
                  ...oldState,
                  lock_order: e.target.checked,
                }))
              }}
              value={Boolean(pathDetailsValues?.lock_order)}
              checked={Boolean(pathDetailsValues?.lock_order)}
            />
          </Box>
          <Box marginTop="8px" width="48%">
            <Typography color="secondary" className={classes.inputLabel}>
              Discover Page
            </Typography>
            <Switch
              onChange={(e) => {
                setPathDetailsValues((oldState) => ({
                  ...oldState,
                  discover_page: e.target.checked,
                }))
              }}
              value={Boolean(pathDetailsValues?.discover_page)}
              checked={Boolean(pathDetailsValues?.discover_page)}
            />
          </Box>
          <Box marginTop="8px" width="48%">
            <Typography color="secondary" className={classes.inputLabel}>
              Is Public
            </Typography>
            <Switch
              onChange={(e) => {
                setPathDetailsValues((oldState) => ({
                  ...oldState,
                  is_public: e.target.checked,
                }))
              }}
              value={Boolean(pathDetailsValues?.is_public)}
              checked={Boolean(pathDetailsValues?.is_public)}
            />
          </Box>
          <Box marginTop="8px" width="48%">
            <Typography color="secondary" className={classes.inputLabel}>
              Color
            </Typography>
            <TextField
              value={pathDetailsValues?.color || ""}
              style={{ marginBottom: "10px" }}
              onChange={(e) => {
                setPathDetailsValues((oldState) => ({
                  ...oldState,
                  color: e.target.value,
                }))
              }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
                endAdornment: (
                  <Box
                    borderRadius="50%"
                    width="15px"
                    height="15px"
                    style={{ backgroundColor: pathDetailsValues?.color }}
                  />
                ),
              }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </div>
      <Box display="flex" flexDirection={"column"} width={"100%"}>
        {renderCategories()}
      </Box>

      {/* <Box className={classes.switchContainer}>
        <Box marginTop="8px" width="48%">
          <Typography color="secondary" className={classes.inputLabel}>
            Lock Order
          </Typography>
          <Switch
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                lock_order: e.target.checked,
              }))
            }}
            value={Boolean(pathDetailsValues?.lock_order)}
            checked={Boolean(pathDetailsValues?.lock_order)}
          />
        </Box>
        <Box marginTop="8px" width="48%">
          <Typography color="secondary" className={classes.inputLabel}>
            Discover Page
          </Typography>
          <Switch
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                discover_page: e.target.checked,
              }))
            }}
            value={Boolean(pathDetailsValues?.discover_page)}
            checked={Boolean(pathDetailsValues?.discover_page)}
          />
        </Box>
        <Box marginTop="8px" width="48%">
          <Typography color="secondary" className={classes.inputLabel}>
            Is Public
          </Typography>
          <Switch
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                is_public: e.target.checked,
              }))
            }}
            value={Boolean(pathDetailsValues?.is_public)}
            checked={Boolean(pathDetailsValues?.is_public)}
          />
        </Box>
        <Box marginTop="8px" width="48%">
          <Typography color="secondary" className={classes.inputLabel}>
            Color
          </Typography>
          <TextField
            value={pathDetailsValues?.color || ""}
            style={{ marginBottom: "10px" }}
            onChange={(e) => {
              setPathDetailsValues((oldState) => ({
                ...oldState,
                color: e.target.value,
              }))
            }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
              endAdornment: (
                <Box
                  borderRadius="50%"
                  width="15px"
                  height="15px"
                  style={{ backgroundColor: pathDetailsValues?.color }}
                />
              ),
            }}
            variant="filled"
            size="small"
            fullWidth
          />
        </Box>
      </Box> */}

      <Box display="flex" justifyContent="space-between" className="w-full">
        <Box className={classes.thumbnailSectionContainer}>
          <Box className={classes.thumbnailContainer}>
            <Typography color="secondary" className={classes.inputLabel}>
              Hero Image
            </Typography>
            <ThumbnailUpload
              title="Drop hero image here"
              cloudinaryUploadPreset={
                import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
              }
              value={path?.thumbnail}
              onUpload={setThumbnail}
              type="thumbnail"
            />
            {Object.keys(path?.thumbnail)?.length === 0 && tryPublish ? (
              <Typography className={classes.requiredText}>
                Hero Image is required
              </Typography>
            ) : null}
          </Box>
          <Box className={classes.thumbnailContainer}>
            <Typography color="secondary" className={classes.inputLabel}>
              Badge
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center">
              <ThumbnailUpload
                title="Drop badge here"
                cloudinaryUploadPreset={
                  import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                }
                value={path?.badge_image}
                type="badge_image"
                onUpload={setThumbnail}
              />
            </Box>
            {Object.keys(path?.badge_image)?.length === 0 && tryPublish ? (
              <Typography className={classes.requiredText}>
                Badge is required
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
      <ClientMultiSelect
        label="Clients"
        id="clients"
        options={clients ? [...clients] : []}
        className="min-h-[32px]"
        handleChange={updateClients}
        selected={pathDetailsValues?.clients || []}
      />
      {/* <Box marginTop="8px" display="flex" alignItems="center">
        <Typography color="secondary" className={classes.inputLabel}>
          Private
        </Typography>
        <Switch
          onChange={(e) => {
            setPathDetailsValues((oldState) => ({
              ...oldState,
              is_public: e.target.checked,
            }))
          }}
          value={pathDetailsValues?.is_public}
          checked={pathDetailsValues?.is_public}
        />
        <Typography color="secondary" className={classes.inputLabel}>
          Public
        </Typography>
      </Box> */}
    </Box>
  )
}

PathDetails.propTypes = {
  tags: PropTypes.array,
}

export default PathDetails
